import * as React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom-v5-compat';
import queryString from 'query-string';

import BySkill from './by_skill';
import ByStudent from './by_student';

import { StudentPracticeInterface, } from '../interfaces'
import { DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE, NO_CLASSROOM, ALL, diagnosticTypeOptions, timeframeOptions, LAST_SCHOOL_YEAR_STARTER_DIAGNOSTIC_OPTION, forwardSlash, } from '../shared'
import { requestGet, } from '../../../../../../modules/request'
import {
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  Spinner,
  MobileSidebarButtonContainer,
} from '../../../../../Shared/index';

const fileDocumentImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/file-document.svg`;

interface StudentPractiesByDiagnosticTypeInterface {
  [key:number]: StudentPracticeInterface[];
}

const ALL_STUDENTS_OPTION = { value: ALL, label: 'All Students'}
const ALL_SKILLS_OPTION = { value: ALL, label: 'All Skills'}

const PracticeInsights = ({ openMobileSidebar, classroom, selectedDiagnostic, setSelectedDiagnostic, selectedSkill, setSelectedSkill, manuallyChangedSelectedDiagnostic, }) => {
  const location = useLocation();

  const timeframeFromLocalStorage = timeframeOptions.find(timeframe => timeframe.value === window.localStorage.getItem(DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE))

  const [selectedStudent, setSelectedStudent] = React.useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = React.useState(timeframeFromLocalStorage || timeframeOptions[0]);
  const [studentPracticesByDiagnostic, setStudentPracticesByDiagnosticType] = React.useState<StudentPractiesByDiagnosticTypeInterface>({})
  const [additionalRequestRunning, setAdditionalRequestRunning] = React.useState<boolean>(false)

  const byStudent = location.pathname.includes('by_student');

  const classroomIdRef = React.useRef(classroom?.id);
  const studentPracticesByDiagnosticRef = React.useRef(studentPracticesByDiagnostic)

  React.useEffect(() => {
    classroomIdRef.current = classroom?.id;
  }, [classroom?.id]);

  React.useEffect(() => {
    studentPracticesByDiagnosticRef.current = studentPracticesByDiagnostic;
  }, [studentPracticesByDiagnostic]);

  React.useEffect(() => {
    if (!classroom) { return }

    setStudentPracticesByDiagnosticType({})
    getStudentPracticesByDiagnosticType(selectedDiagnostic.value, getDataForLastSchoolYearStarterDiagnosticIfNoDataForThisYear)
  }, [classroom, selectedTimeframe])

  React.useEffect(() => {
    if (!classroom) { return }

    if (studentPracticesByDiagnostic[selectedDiagnostic.value]) { return }

    getStudentPracticesByDiagnosticType(selectedDiagnostic.value, getDataForLastSchoolYearStarterDiagnosticIfNoDataForThisYear)
  }, [selectedDiagnostic])

  React.useEffect(() => {
    window.localStorage.setItem(DIAGNOSTIC_INSIGHTS_SELECTED_TIMEFRAME_VALUE, selectedTimeframe.value.toString())
  }, [selectedTimeframe])

  React.useEffect(() => {
    if (!byStudent || !studentPracticesByDiagnostic[selectedDiagnostic.value]) { return }

    if (selectedSkill && skillOptions()?.find(skill => skill.value === selectedSkill.value)) { return }

    setSelectedSkill(skillOptions()[0])
  }, [byStudent, selectedDiagnostic, studentPracticesByDiagnostic])

  React.useEffect(() => {
    if (byStudent || !studentPracticesByDiagnostic[selectedDiagnostic.value]) { return }

    if (selectedStudent && studentOptions()?.find(student => student.value === selectedStudent.value)) { return }

    setSelectedStudent(studentOptions()[0])
  }, [byStudent, selectedDiagnostic, studentPracticesByDiagnostic])

  function allSkillNames() {
    const studentPractices = studentPracticesByDiagnostic[selectedDiagnostic.value]
    if (!studentPractices) { return null }

    return [...new Set(studentPractices.map(sp => sp.skills).flat().map(skill => skill.skill_name))]
  }

  function skillOptions() {
    const options = allSkillNames()
      ?.map(skillName => ({ value: skillName, label: skillName, }))
      ?.sort((skillNameA, skillNameB) => skillNameA.label.localeCompare(skillNameB.label)) || []

    return [ALL_SKILLS_OPTION].concat(options)
  }

  function studentOptions() {
    const studentPractices = studentPracticesByDiagnostic[selectedDiagnostic.value]
    if (!studentPractices) { return null }

    const options = studentPractices?.map(student => ({ value: student.user_id, label: student.user_name, }))
    return [ALL_STUDENTS_OPTION].concat(options)
  }

  function getStudentPracticesByDiagnosticType(selectedDiagnosticId, callback=null) {
    const requestedClassroomId = classroom?.id;

    if (requestedClassroomId === NO_CLASSROOM.id) {
      setStudentPracticesByDiagnosticType({...studentPracticesByDiagnosticRef.current, [selectedDiagnosticId]: []})
      return
    }
    
    const queryHash = { timeframe: selectedTimeframe.value, };

    const url = queryString.stringifyUrl(
      {
        query: queryHash,
        url: `/diagnostic_insights/skill_practice_by_classroom/${requestedClassroomId}/${selectedDiagnosticId}`,
      },
      { arrayFormat: 'bracket' }
    );

    requestGet(url, (body) => {
      // guards against overwriting the current classroom's data with a request that was fired earlier
      if (classroomIdRef.current !== requestedClassroomId) { return }

      if (callback) { callback(body)}

      // using ref here guarantees other diagnostic levels don't get overwritten, regardless of when the requests fire and return
      setStudentPracticesByDiagnosticType({...studentPracticesByDiagnosticRef.current, [selectedDiagnosticId]: body});
    });
  }

  function getDataForLastSchoolYearStarterDiagnosticIfNoDataForThisYear(students) {
    if (manuallyChangedSelectedDiagnostic || students.find(student => student.skills?.length)) { return }

    setAdditionalRequestRunning(true)
    getStudentPracticesByDiagnosticType(LAST_SCHOOL_YEAR_STARTER_DIAGNOSTIC_OPTION.value, changeDefaultDiagnosticToLastSchoolYearIfData)
  }

  function changeDefaultDiagnosticToLastSchoolYearIfData(students) {
    setAdditionalRequestRunning(false)

    if (manuallyChangedSelectedDiagnostic || !students.find(student => student.skills?.length)) { return }

    setSelectedDiagnostic(LAST_SCHOOL_YEAR_STARTER_DIAGNOSTIC_OPTION, false)
  }

  const renderSkillDropdown = () => {
    const disabled = !studentOptions()?.length

    return (
      <DropdownInput
        className={`small bordered ${disabled ? 'disabled' : ''}`}
        disabled={disabled}
        handleChange={setSelectedSkill}
        id="skill-dropdown"
        isSearchable={true}
        label='Skill'
        options={skillOptions()}
        value={skillOptions()?.find(skillOption => skillOption.value === selectedSkill?.value)}
      />
    )
  }

  const renderStudentDropdown = () => {
    const disabled = !studentOptions()?.length

    return (
      <DropdownInput
        className={`small bordered ${disabled ? 'disabled' : ''}`}
        disabled={disabled}
        handleChange={setSelectedStudent}
        id="student-dropdown"
        isSearchable={true}
        label='Student'
        options={studentOptions()}
        value={studentOptions()?.find(studentOption => studentOption.value === selectedStudent?.value)}
      />
    )
  }

  if (!classroom) {
    return <Spinner />;
  }

  const diagnosticDropdown = (
    <DropdownInput
      className="small bordered"
      handleChange={setSelectedDiagnostic}
      id="diagnostic-type-dropdown"
      isSearchable={true}
      label='Diagnostic'
      options={diagnosticTypeOptions}
      value={selectedDiagnostic}
    />
  )

  const timeframeDropdown = (
    <DropdownInput
      className="small bordered"
      handleChange={setSelectedTimeframe}
      id="timeframe-dropdown"
      isSearchable={true}
      label='Timeframe'
      options={timeframeOptions}
      value={selectedTimeframe}
    />
  )

  const renderSubheader = () => {
    return (
      <div className="subheader">
        <div className="dropdown-controls">
          {diagnosticDropdown}
          {byStudent && renderSkillDropdown()}
          {!byStudent && renderStudentDropdown()}
          {timeframeDropdown}
        </div>
      </div>
    )
  }

  const routes = (
    <Routes>
      <Route
        element={
          <BySkill
            additionalRequestRunning={additionalRequestRunning}
            allSkillNames={allSkillNames()}
            selectedStudent={selectedStudent}
            studentPractices={studentPracticesByDiagnostic[selectedDiagnostic.value]}
          />
        }
        path="by_skill"
      />
      <Route
        element={
          <ByStudent
            additionalRequestRunning={additionalRequestRunning}
            selectedSkill={selectedSkill}
            studentPractices={studentPracticesByDiagnostic[selectedDiagnostic.value]}
          />
        }
        path="by_student"
      />
    </Routes>
  )

  return (
    <main>
      <header>
        <h1>
          <b>Diagnostic Insights</b>
          {forwardSlash}
          Practice Insights
          {forwardSlash}
          {byStudent ? 'By Student' : 'By Skill'}
        </h1>
        <a className="quill-button outlined small grey" href='https://support.quill.org/en/articles/10751323-how-do-teachers-use-diagnostic-insights' rel="noopener noreferrer" target="_blank">
          <img alt="" src={fileDocumentImgSrc} />
          Guide
        </a>
      </header>
      <MobileSidebarButtonContainer openMobileSidebar={openMobileSidebar} />
      {renderSubheader()}
      {routes}
    </main>
  );
};

export default PracticeInsights;
